import { getVoiceGender } from './genderUtils';
import api from '../services/api';
import { TTS_ENGINES, DEFAULT_LOCALE } from '../constants/tts';

let currentEngine = TTS_ENGINES.BROWSER;
let stopRequested = false;
let skipAllRequested = false;
let activeAudio = null;
let voices = [];
let prefetchCache = new Map(); // Map<segmentIndex, { blobUrl, status, promise }>
let totalSegments = [];
let currentPlayIndex = 0;
const LOOKAHEAD_COUNT = 2; // Prefetch N segments ahead

const loadVoices = () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
        voices = window.speechSynthesis.getVoices();
    }
};

if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.onvoiceschanged = loadVoices;
    loadVoices();
}

export const setEngine = (engine) => {
    currentEngine = engine;
};

export const getEngine = () => currentEngine;

export const getAvailableVoices = async (engine = currentEngine) => {
    if (engine === TTS_ENGINES.BROWSER) {
        if (voices.length === 0) {
            loadVoices();
        }
        return voices;
    } else {
        try {
            const response = await api.getTTSVoices(engine);
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch ${engine} voices: `, error);
            return [];
        }
    }
};

export const getGenderAppropriateVoices = async (engine, patientGender, locale = DEFAULT_LOCALE) => {
    const allVoices = await getAvailableVoices(engine);
    let patientVoice = null;
    let narratorVoice = null;

    if (engine === TTS_ENGINES.EDGE) {
        patientVoice = patientGender.toLowerCase() === 'male' ? 'en-GB-RyanNeural' : 'en-GB-SoniaNeural';
        narratorVoice = patientGender.toLowerCase() === 'male' ? 'en-GB-SoniaNeural' : 'en-GB-RyanNeural';
    } else {
        // Find best match for patient
        const pVoiceObj = await findBestVoice(patientGender, locale, { engine });
        patientVoice = pVoiceObj?.id || pVoiceObj?.voiceURI || null;

        // Find best match for narrator (opposite gender)
        const oppositeGender = patientGender.toLowerCase() === 'male' ? 'female' : 'male';
        const nVoiceObj = await findBestVoice(oppositeGender, locale, { engine, excludedVoiceId: patientVoice });
        narratorVoice = nVoiceObj?.id || nVoiceObj?.voiceURI || null;
    }

    return { patientVoice, narratorVoice };
};

const findBestVoiceInList = (voiceList, targetGender) => {
    // Exact gender match
    const genderMatches = voiceList.filter(v => (v.gender || getVoiceGender(v)) === targetGender);
    if (genderMatches.length > 0) {
        return genderMatches[Math.floor(Math.random() * genderMatches.length)];
    }

    // Any voice as fallback
    return voiceList[Math.floor(Math.random() * voiceList.length)] || null;
};

export const findBestVoice = async (targetGender, locale = DEFAULT_LOCALE, options = {}) => {
    const engine = options.engine || currentEngine;
    const excludedId = options.excludedVoiceId || options.excludedVoiceURI;

    const allVoices = await getAvailableVoices(engine);

    // Filter by locale first
    let localeVoices = allVoices.filter(v => (v.lang || v.locale || '').toLowerCase().startsWith(locale.toLowerCase()));
    if (localeVoices.length === 0) {
        localeVoices = allVoices.filter(v => (v.lang || v.locale || '').toLowerCase().startsWith('en'));
    }

    if (localeVoices.length === 0) localeVoices = allVoices;

    // Exclude voice if requested
    const filteredVoices = excludedId ? localeVoices.filter(v => (v.id || v.voiceURI) !== excludedId) : localeVoices;

    return findBestVoiceInList(filteredVoices, targetGender);
};

const speakBrowser = (text, voiceURI, options) => {
    return new Promise((resolve) => {
        const utterance = new SpeechSynthesisUtterance(text);
        if (voiceURI) {
            const voice = voices.find(v => v.voiceURI === voiceURI);
            if (voice) utterance.voice = voice;
        }

        utterance.rate = options.rate || 1.0;
        utterance.pitch = options.pitch || 1.0;
        utterance.onend = () => resolve();
        utterance.onerror = () => resolve();

        window.speechSynthesis.speak(utterance);
    });
};

const speakServer = async (text, voiceId, options) => {
    try {
        const blob = await api.synthesizeTTS(text, voiceId, options.rate || 1.0, currentEngine);
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        activeAudio = audio;

        return new Promise((resolve, reject) => {
            audio.onended = () => {
                URL.revokeObjectURL(url);
                activeAudio = null;
                resolve();
            };
            audio.onerror = (e) => {
                URL.revokeObjectURL(url);
                activeAudio = null;
                reject(e);
            };

            if (stopRequested) {
                URL.revokeObjectURL(url);
                activeAudio = null;
                resolve();
                return;
            }

            audio.play().catch(err => {
                console.error('Audio Play Error:', err);
                activeAudio = null;
                resolve(); // Don't block sequence on play error
            });
        });
    } catch (error) {
        console.error('Server TTS Error:', error);
        return Promise.resolve(); // Don't block sequence
    }
};

export const speak = async (text, voiceId = null, options = {}) => {
    if (!text) return;
    stopRequested = false;
    skipAllRequested = false;

    if (currentEngine === TTS_ENGINES.BROWSER) {
        return speakBrowser(text, voiceId, options);
    } else {
        return speakServer(text, voiceId, options);
    }
};

const prefetchSegment = (index, segment) => {
    if (prefetchCache.has(index)) {
        return prefetchCache.get(index).promise;
    }

    if (!segment.text?.trim()) {
        // Return resolved promise for empty segments
        const entry = { status: 'ready', blobUrl: null, promise: Promise.resolve(null) };
        prefetchCache.set(index, entry);
        return entry.promise;
    }

    const entry = { status: 'pending', blobUrl: null, promise: null };

    entry.promise = (async () => {
        try {
            const blob = await api.synthesizeTTS(
                segment.text,
                segment.voiceId || segment.voiceURI,
                segment.options?.rate || 1.0,
                currentEngine
            );
            const blobUrl = URL.createObjectURL(blob);
            entry.blobUrl = blobUrl;
            entry.status = 'ready';
            return blobUrl;
        } catch (error) {
            console.error(`Prefetch error for segment ${index}:`, error);
            entry.status = 'error';
            throw error;
        }
    })();

    prefetchCache.set(index, entry);
    return entry.promise;
};

const prefetchAhead = (fromIndex) => {
    for (let i = fromIndex; i < Math.min(fromIndex + LOOKAHEAD_COUNT, totalSegments.length); i++) {
        if (!prefetchCache.has(i)) {
            prefetchSegment(i, totalSegments[i]);
        }
    }
};

const playSegmentFromCache = async (index) => {
    if (skipAllRequested || stopRequested) return;

    const segment = totalSegments[index];

    // For browser engine, use existing speakBrowser() directly (no prefetch support)
    if (currentEngine === TTS_ENGINES.BROWSER) {
        await speakBrowser(segment.text, segment.voiceId || segment.voiceURI, segment.options || {});
        return;
    }

    try {
        // Wait for prefetch to complete if it's already in flight
        let cacheEntry = prefetchCache.get(index);
        if (!cacheEntry) {
            // Fallback: fetch synchronously if cache miss
            await prefetchSegment(index, segment);
            cacheEntry = prefetchCache.get(index);
        }

        const blobUrl = await cacheEntry.promise;

        if (skipAllRequested || stopRequested || !blobUrl) return;

        const audio = new Audio(blobUrl);
        activeAudio = audio;

        return new Promise((resolve) => {
            audio.onended = () => {
                activeAudio = null;
                resolve();
            };
            audio.onerror = (e) => {
                console.error(`Audio playback error for segment ${index}:`, e);
                activeAudio = null;
                resolve(); // Don't block sequence
            };

            if (skipAllRequested || stopRequested) {
                activeAudio = null;
                resolve();
                return;
            }

            audio.play().catch(err => {
                console.error('Audio Play Error:', err);
                activeAudio = null;
                resolve();
            });
        });
    } catch (error) {
        console.error(`Failed to play segment ${index} from cache:`, error);
        return Promise.resolve();
    }
};

export const speakSequence = async (segments) => {
    // Reset state
    skipAllRequested = false;
    stopRequested = false;

    // Clear existing cache items (revoke URLs)
    for (const entry of prefetchCache.values()) {
        if (entry.blobUrl) {
            URL.revokeObjectURL(entry.blobUrl);
        }
    }
    prefetchCache.clear();

    totalSegments = segments;
    currentPlayIndex = 0;

    if (segments.length === 0) return;

    // Start prefetching first batch
    prefetchAhead(0);

    for (let i = 0; i < segments.length; i++) {
        if (skipAllRequested || stopRequested) break;
        currentPlayIndex = i;

        // Trigger prefetch for next batch before playing current
        prefetchAhead(i + 1);

        await playSegmentFromCache(i);
    }

    // Cleanup: revoke all blob URLs after complete (or partial) sequence
    for (const entry of prefetchCache.values()) {
        if (entry.blobUrl) {
            URL.revokeObjectURL(entry.blobUrl);
        }
    }
    prefetchCache.clear();
};

export const stop = () => {
    stopRequested = true;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
    }
    if (activeAudio) {
        activeAudio.pause();
        activeAudio.currentTime = 0;
        activeAudio = null;
    }
};

export const skipAll = () => {
    skipAllRequested = true;
    stopRequested = true;

    if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
    }

    if (activeAudio) {
        activeAudio.pause();
        activeAudio.currentTime = 0;
        activeAudio = null;
    }

    // Cleanup cache immediately
    for (const entry of prefetchCache.values()) {
        if (entry.blobUrl) {
            URL.revokeObjectURL(entry.blobUrl);
        }
    }
    prefetchCache.clear();
};

export const isSpeaking = () => {
    if (activeAudio && !activeAudio.paused) return true;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
        return window.speechSynthesis.speaking;
    }
    return false;
};

export const getPrefetchStatus = () => ({
    cached: prefetchCache.size,
    current: currentPlayIndex,
    total: totalSegments.length
});
